import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import API_CONFIG from '../../apiConfig';
import ProductUpload from './ProductUpload';
import SettingsPanel from './SettingsPanel';
import { examplePresets } from './examplePresets';
import { useCreditEstimation } from '../../hooks/useCreditEstimation';
import CreditCostPreview from '../ui/CreditCostPreview';
import InsufficientCreditsModal from '../ui/InsufficientCreditsModal';

interface LocationState {
  inputImages: string[];
  prompt: string;
  style: string;
  variations: number;
  recreatingFrom: string;
  generatedImages?: string[];
}

const AIPhotoshoot: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;

  // State to track if an image is uploaded
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [generating, setGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<string[]>(state?.generatedImages || []);
  const [prompt, setPrompt] = useState(state?.prompt || '');
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);
  const [isExample, setIsExample] = useState(false);
  const [variations, setVariations] = useState(state?.variations || 1);
  const [sceneStyle, setSceneStyle] = useState(state?.style || 'lifestyle');
  const [mode, setMode] = useState<string>('performance'); // Start with performance (always available)
  const { currentUser, refreshUserCredits, userCredits } = useAuth(); // Get current user from auth context
  const [errorMessage, setErrorMessage] = useState('');
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);

  // Credit estimation hook
  const [creditEstimation, estimateCreditCost] = useCreditEstimation('photoshoot');

  // Effect to handle recreation state
  useEffect(() => {
    const loadInputImages = async () => {
      if (state?.inputImages && state.inputImages.length >= 1) {
        try {
          setImageUrls([state.inputImages[0]]);
          if (state.generatedImages) {
            setGeneratedImages(state.generatedImages);
          }
        } catch (error) {
          console.error('Failed to load input images:', error);
        }
      }
    };

    loadInputImages();
  }, [state]);

  // Clear error messages when user uploads new images
  useEffect(() => {
    if (imageUrls.length > 0) {
      setErrorMessage('');
    }
  }, [imageUrls]);

  // Credit estimation effect - trigger when generation settings change
  useEffect(() => {
    // Only estimate if we have required data and user is authenticated
    if (!currentUser?.uid || imageUrls.length === 0) {
      return;
    }

    // Build the same payload structure as the actual generation
    const estimationPayload = {
      sceneStyle: sceneStyle,
      productImageIds: [],
      colorTheme: 'white',
      inputImageUrls: imageUrls,
      prompt: prompt || 'in a landscape',
      numImages: variations,
      mode: mode.toLowerCase(),
      quality: "high"
    };

    estimateCreditCost(estimationPayload);
  }, [
    currentUser?.uid,
    imageUrls,
    variations,
    mode,
    estimateCreditCost
  ]);

  // Example handling
  const handlePresetSelect = (presetId: number) => {
    const preset = examplePresets.find(p => p.id === presetId);
    if (preset) {
      setPrompt(preset.prompt);
      setSelectedPreset(presetId);
      setGeneratedImages(preset.results);
      setIsExample(true);
      // Set the example image as the current product image
      setImageUrls([preset.preview]);
    }
  };

  const handleGeneratePhotoshoot = async () => {
    if (imageUrls.length === 0) return;

    // Check if user can afford the generation
    if (!creditEstimation.canAfford) {
      setShowInsufficientCreditsModal(true);
      return;
    }

    setGenerating(true);
    setIsExample(false);
    setErrorMessage(''); // Clear any existing error messages
    try {
      const payload = {
        sceneStyle: sceneStyle,
        productImageIds: [],     // No IDs available, send empty array or map if available
        colorTheme: 'white',
        inputImageUrls: imageUrls,
        prompt: prompt || 'in a landscape',
        numImages: variations,
        userId: currentUser?.uid || 'anonymous',      // Use the current user's UID or fallback to anonymous
        mode: mode.toLowerCase(),
        quality: "high"
      };
      const response = await fetch(API_CONFIG.PHOTOSHOOT_GENERATE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      // Check HTTP status
      if (!response.ok) {
        setErrorMessage('Something went wrong. Please try again.');
        return;
      }

      const data = await response.json();

      // Check if images were returned
      if (data.success && Array.isArray(data.imageUrls) && data.imageUrls.length > 0) {
        setGeneratedImages(data.imageUrls);
      // Refresh user credits after successful generation
        await refreshUserCredits();
        setErrorMessage('');
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Failed to generate photoshoot:', error);
      setErrorMessage('Failed to generate photoshoot. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="w-full relative">
      {/* Main Content Area - Three Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 p-2">
        {/* First Column - Product Image Upload */}
        <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
          <div className="p-4 border-b border-accent-taupe flex-shrink-0">
            <h2 className="text-lg font-semibold text-text-heading">Product Image</h2>
            <span className="text-sm text-text-description">Upload a product image to generate photoshoot</span>
          </div>
          
          <div className="p-4 flex flex-col min-h-[400px]">
          <ProductUpload
            imageUrls={imageUrls}
            setImageUrls={setImageUrls}
            isExample={isExample}
            />
          </div>
                </div>

        {/* Second Column - Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
          <div className="p-4 border-b border-accent-taupe flex-shrink-0">
            <h2 className="text-lg font-semibold text-text-heading">Controls</h2>
            <span className="text-sm text-text-description">Configure your AI photoshoot settings</span>
          </div>

          <div className="p-4 space-y-3 flex-1 overflow-y-auto">
            {/* AI Photoshoot Label */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-text-heading">AI Photoshoot</label>
              </div>
            </div>

          {/* Example Styles section */}
            <div className="bg-accent-sand rounded-lg p-3">
              <h4 className="text-sm font-medium text-text-heading mb-2">Example Styles</h4>
              <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
              {examplePresets.map(preset => (
                <div
                  key={preset.id}
                  className={`relative cursor-pointer rounded-lg overflow-hidden border transition-all ${selectedPreset === preset.id ? 'border-secondary-500 shadow-md' : 'border-accent-taupe hover:border-secondary-300'}`}
                  onClick={() => handlePresetSelect(preset.id)}
                >
                    <div className="aspect-ratio-box" style={{ paddingBottom: '60%', position: 'relative' }}>
                    <img
                      src={preset.preview}
                      alt={preset.name}
                      className="absolute inset-0 w-full h-full object-contain bg-accent-sand"
                    />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-1 text-center truncate">
                    {preset.name}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Settings Panel */}
            <div className="bg-accent-sand rounded-lg p-3">
          <SettingsPanel
            prompt={prompt}
            setPrompt={setPrompt}
            variations={variations}
            setVariations={setVariations}
            sceneStyle={sceneStyle}
            setSceneStyle={setSceneStyle}
            mode={mode}
            setMode={setMode}
          />
      </div>

            {/* Credit Cost Preview */}
            <div className="bg-accent-sand rounded-lg p-2">
        <CreditCostPreview
          creditCost={creditEstimation.creditCost}
          isEstimating={creditEstimation.isEstimating}
          error={creditEstimation.error}
          canAfford={creditEstimation.canAfford}
                className="text-xs"
        />
            </div>

            {/* Clear Button */}
            <button
              onClick={() => {
                // Clear selected preset
                setSelectedPreset(null);
                // Clear prompt and reset settings
                setPrompt('');
                setSceneStyle('lifestyle');
                setVariations(1);
                setMode('performance');
                // Clear generated images and examples
                setGeneratedImages([]);
                setIsExample(false);
                setErrorMessage('');
                // Clear uploaded images
                setImageUrls([]);
              }}
              className="w-full px-4 py-2.5 rounded-lg font-medium text-sm transition-colors bg-red-100 hover:bg-red-200 text-red-700 border border-red-300 mb-3"
              title="Clear all settings, images, and results"
            >
              Clear All Settings
            </button>

            {/* Generate button */}
            <button
              className={`w-full px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${imageUrls.length > 0
            ? 'bg-secondary-500 hover:bg-secondary-600 text-white cursor-pointer'
            : 'bg-gray-300 text-text-description cursor-not-allowed'
            }`}
          onClick={handleGeneratePhotoshoot}
          disabled={imageUrls.length === 0 || generating}
        >
          {generating ? (
                <div className="flex items-center justify-center space-x-2">
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              <span>Generating...</span>
            </div>
          ) : (
            <>
              <i className="fas fa-magic mr-2"></i>
              Generate Photoshoot
            </>
          )}
        </button>
        {imageUrls.length === 0 && (
              <p className="text-xs text-text-description text-center">Upload an image to enable generation</p>
            )}

            {/* Error message display */}
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs text-red-700">{errorMessage}</p>
                </div>
              </div>
        )}
      </div>
        </div>

        {/* Third Column - Generated Result */}
        <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
          <div className="p-4 border-b border-accent-taupe flex-shrink-0">
            <h2 className="text-lg font-semibold text-text-heading">Generated Results</h2>
            <span className="text-sm text-text-description">
              {isExample ? 'Example results will appear here' : 'Generated photoshoot images will appear here'}
            </span>
          </div>

          <div className="p-4 flex-1 flex flex-col">
            {generating ? (
              <div className="flex-1 bg-accent-sand rounded-lg border border-accent-taupe flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-500 mx-auto mb-4"></div>
                  <p className="text-sm text-text-description">Generating photoshoot...</p>
                  <p className="text-xs text-text-description mt-2">This may take a few moments</p>
                </div>
              </div>
            ) : generatedImages.length > 0 ? (
              <div className="relative flex-1 min-h-[400px]">
                <div className={`${generatedImages.length === 1 ? 'grid grid-cols-1' : 'grid grid-cols-1 sm:grid-cols-2'} gap-2 h-[400px] overflow-y-auto`}>
                  {generatedImages.slice(0, 4).map((url, idx) => (
                    <div key={idx} className="relative bg-accent-sand rounded-lg border border-accent-taupe overflow-hidden flex flex-col">
                      <img
                        src={url}
                        alt={`${isExample ? 'Example' : 'Generated'} ${idx + 1}`}
                        className="w-full flex-1 object-contain"
                      />
                      <div className="absolute top-1 left-1 bg-secondary-500 text-white text-xs font-semibold px-1.5 py-0.5 rounded">
                        #{idx + 1}
                      </div>
                      {/* Action bar */}
                      <div className="flex justify-center items-center gap-2 px-2 py-2 bg-white border-t border-accent-taupe">
                        {/* Download Button */}
                        <button
                          className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-2 py-1 rounded-lg transition-colors text-xs font-medium"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `${isExample ? 'example' : 'generated'}-image-${idx + 1}.jpg`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          Download
                        </button>
                        {/* Generate Video Button */}
                        <button
                          className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-2 py-1 rounded-lg transition-colors text-xs font-medium"
                          onClick={() => {
                            // TODO: Implement video generation logic
                            alert('Video generation feature coming soon!');
                          }}
                        >
                          Create Video
                        </button>
                        {/* Upscale Button */}
                        <button
                          className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-2 py-1 rounded-lg transition-colors text-xs font-medium"
                          onClick={() => {
                            navigate('/canvas-editor', {
                              state: {
                                inputImages: [url],
                                prompt: '',
                                style: '',
                                variations: 1,
                                recreatingFrom: '',
                                tool: 'upscaler'
                              }
                            });
                          }}
                        >
                          HD
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {generatedImages.length > 4 && (
                  <div className="absolute bottom-2 right-2 bg-secondary-500 text-white text-xs px-2 py-1 rounded">
                    +{generatedImages.length - 4} more
                  </div>
                )}
                
                {/* Example notice */}
                {isExample && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start">
                      <i className="fas fa-info-circle text-blue-600 mr-2 mt-0.5"></i>
                      <div>
                        <p className="text-xs text-blue-800 font-medium">Example Results</p>
                        <p className="text-xs text-blue-700 mt-1">
                          These are example photoshoot images. Upload your own product image to generate real photoshoot images.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 bg-accent-sand rounded-lg border border-accent-taupe flex items-center justify-center min-h-[400px]">
                <div className="text-center text-text-description">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm">Upload a product image and click Generate to see results</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        requiredCredits={creditEstimation.creditCost || 0}
        availableCredits={userCredits}
        featureName="AI Photoshoot"
      />
    </div>
  );
};

export default AIPhotoshoot; 